// src/components/ambassador/ProspectDetail.jsx
//
// /ambassador/prospects/:id - one prospect and what the ambassador can do
// with it right now:
//   available / my follow-up → Réserver
//   reserved for me          → countdown, Itinéraire, Je suis sur place,
//                              Prolonger (near it, once), Annuler
//   my follow-up             → also Je suis sur place directly
//   checked in               → the visit result form (comment required when
//                              the GPS didn't match, or for "Autre")
//   reserved / my follow-up  → Créer l'établissement (on site, before the owner
//                              has an account; owner e-mail optional)
//   its facility (mine)      → kiosk status and Transférer au gérant
// plus the visit history (others' visits without their name).
//
// The phone's position is sent only for the check-in and the extension.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Paper, Stack, Button, Box, Chip, Alert, CircularProgress, Radio, RadioGroup,
  FormControlLabel, TextField, Divider, Link,
} from '@mui/material';
import { useAmbassador } from './AmbassadorApp';
import useNow from './useNow';
import { ambassadorGet, ambassadorPost } from '../../utils/ambassadorApi';
import { AVAILABILITY, VISIT_RESULTS, RESULT_LABEL, TYPE_LABEL, formatRemaining, errorText } from '../../utils/ambassadorStatus';
import FacilityTransferCard from '../facilityTransfer/FacilityTransferCard';

/** The phone's position, or null if refused / unavailable within 15 s. */
const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 });
});

const GPS_TEXT = {
  OK: null,
  WARNING: 'Votre position était un peu éloignée de l’établissement : la visite est enregistrée et sera vérifiée.',
  REVIEW: 'Votre position ne correspond pas à l’établissement (ou n’était pas disponible) : expliquez en commentaire, un administrateur vérifiera la visite.',
};
const day = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

export default function ProspectDetail() {
  const { id } = useParams();
  const { me, getToken, reloadMe } = useAmbassador();
  const now = useNow();
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [visit, setVisit] = useState(null);       // { visitId, gpsCheck, distanceM }
  const [result, setResult] = useState('');
  const [comment, setComment] = useState('');
  const [done, setDone] = useState(null);         // outcome after a result
  const answered = useRef(new Set());              // visits just completed, until /me catches up
  const [creating, setCreating] = useState(false);  // the "Créer l'établissement" form is open
  const [ownerEmail, setOwnerEmail] = useState('');
  const [created, setCreated] = useState(null);

  const load = useCallback(() => ambassadorGet(`/prospects/${id}`, getToken())
    .then(setP).catch((e) => setError(errorText(e))), [id, getToken]);
  useEffect(() => { load(); }, [load]);

  // A check-in made earlier (another screen, page reload) still waiting for its result.
  useEffect(() => {
    const open = me.openVisits.find((v) => v.prospectId === Number(id));
    if (open && !visit && !answered.current.has(open.visitId)) setVisit({ visitId: open.visitId, gpsCheck: open.gpsCheck, distanceM: null });
  }, [me, id, visit]);

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); await Promise.all([load(), reloadMe()]); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  const reserve = () => run(() => ambassadorPost(`/prospects/${id}/reserve`, getToken()));
  const release = () => {
    if (!window.confirm('Annuler la réservation ? L’établissement redevient disponible pour les autres.')) return;
    run(() => ambassadorPost(`/prospects/${id}/release`, getToken()));
  };
  const extend = () => run(async () => {
    const pos = await getPosition();
    if (!pos) throw new Error('POSITION_REQUIRED');
    await ambassadorPost(`/prospects/${id}/extend`, getToken(), pos);
  });
  const checkIn = () => run(async () => {
    const pos = await getPosition();
    if (!pos && !window.confirm('Position indisponible. Continuer sans position ? Un commentaire vous sera demandé.')) return;
    const r = await ambassadorPost(`/prospects/${id}/checkin`, getToken(), pos ?? { noPosition: true });
    setVisit(r);
  });
  const submit = () => run(async () => {
    const outcome = await ambassadorPost(`/visits/${visit.visitId}/result`, getToken(), { result, comment: comment.trim() || undefined });
    answered.current.add(visit.visitId);
    setDone({ result, ...outcome });
    setVisit(null); setResult(''); setComment('');
  });

  const createFacility = () => run(async () => {
    const r = await ambassadorPost(`/prospects/${id}/facility`, getToken(), { ownerEmail: ownerEmail.trim() || undefined });
    setCreated(r); setCreating(false); setOwnerEmail('');
  });

  if (!p && !error) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!p) return <Container sx={{ py: 3 }}><Alert severity="error">{error}</Alert></Container>;

  const a = p.availability;
  const meta = AVAILABILITY[a] ?? AVAILABILITY.DO_NOT_CONTACT;
  const commentRequired = visit?.gpsCheck === 'REVIEW' || result === 'OTHER';
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}`;

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Button component={RouterLink} to="/ambassador/map" size="small" sx={{ mb: 1 }}>← Carte</Button>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{p.name}</Typography>
        <Typography color="text.secondary">{[p.address, p.postalCode, p.city].filter(Boolean).join(' ')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {[p.facilityType && (TYPE_LABEL[p.facilityType] ?? p.facilityType), p.territory].filter(Boolean).join(' · ')}
        </Typography>
        {p.phone && <Typography variant="body2"><Link href={`tel:${p.phone}`}>{p.phone}</Link></Typography>}
        <Chip label={meta.label} sx={{ mt: 1.5, bgcolor: meta.color, color: '#fff', fontWeight: 600 }} />
        {p.pendingReview && <Chip label="Fiche en cours de vérification" size="small" sx={{ mt: 1.5, ml: 1 }} />}
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {done && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDone(null)}>
          Visite enregistrée : {RESULT_LABEL[done.result]}.
          {done.followUpUntil && ` L’établissement vous est réservé pour la relance jusqu’au ${day(done.followUpUntil)}.`}
          {done.result === 'REFUSED' && done.refusedUntil && ` Il ne sera plus proposé avant le ${day(done.refusedUntil)}.`}
          {done.doNotContact && ' Il est retiré de la carte.'}
        </Alert>
      )}

      {created && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
          Établissement créé (n° {created.facilityId}). Vous pouvez associer la borne : scannez le QR qu’elle affiche.
          {created.transfer && ` Invitation envoyée à ${created.transfer.transfer.toEmail}.`}
          {created.transferError && ' L’invitation n’est pas partie (e-mail invalide ?) : renvoyez-la ci-dessous.'}
        </Alert>
      )}

      {/* Its facility, once created */}
      {p.facility && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Établissement QRProofs n° {p.facility.id}</Typography>
            <Typography variant="body2">
              {p.facility.hasKiosk
                ? `Borne associée${p.facility.kioskLastSeen ? `, vue le ${new Date(p.facility.kioskLastSeen).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}.`
                : 'Pas encore de borne : branchez-la, donnez-lui le Wi-Fi, puis scannez le QR qu’elle affiche.'}
            </Typography>
            {!p.facility.authorized && (
              <Typography variant="body2" color="text.secondary">
                En attente de validation par QRProofs. Tant qu’il est à votre nom, la borne et les statistiques
                fonctionnent : vous pouvez faire une démo de scan.
              </Typography>
            )}
          </Paper>
          <FacilityTransferCard facilityId={p.facility.id} ownerEmail={p.facility.ownedByMe ? `${p.facility.ownerEmail} (vous, jusqu’au transfert)` : p.facility.ownerEmail}
            getToken={getToken} onChange={load} />
        </Stack>
      )}

      {/* Result form */}
      {visit && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: '#0b4dff', borderWidth: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Comment s’est passée la visite ?</Typography>
          {GPS_TEXT[visit.gpsCheck] && <Alert severity={visit.gpsCheck === 'REVIEW' ? 'warning' : 'info'} sx={{ mb: 1 }}>{GPS_TEXT[visit.gpsCheck]}</Alert>}
          <RadioGroup value={result} onChange={(e) => setResult(e.target.value)}>
            {VISIT_RESULTS.map(([k, label]) => <FormControlLabel key={k} value={k} control={<Radio />} label={label} />)}
          </RadioGroup>
          <TextField fullWidth multiline minRows={2} sx={{ mt: 1 }} value={comment} onChange={(e) => setComment(e.target.value)}
            label={commentRequired ? 'Commentaire (obligatoire)' : 'Commentaire (facultatif)'}
            helperText="Ex. : gérant présent le matin, rappeler après 15 h." inputProps={{ maxLength: 1000 }} />
          <Button variant="contained" fullWidth size="large" sx={{ mt: 2 }}
            disabled={busy || !result || (commentRequired && !comment.trim())} onClick={submit}>
            Enregistrer la visite
          </Button>
        </Paper>
      )}

      {/* Actions */}
      {!visit && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {a === 'RESERVED_BY_ME' && (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Réservé pour vous encore</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatRemaining(p.reservedUntil, now)}</Typography>
            </Paper>
          )}
          {a === 'FOLLOW_UP_MINE' && (
            <Alert severity="info">Votre relance, jusqu’au {day(p.followUpUntil)} : personne d’autre ne peut le démarcher.</Alert>
          )}
          {(a === 'RESERVED_BY_ME' || a === 'FOLLOW_UP_MINE') && (
            <>
              <Button variant="contained" size="large" disabled={busy} onClick={checkIn} sx={{ py: 1.5, fontWeight: 700 }}>
                {busy ? 'Localisation…' : 'Je suis sur place'}
              </Button>
              <Button variant="outlined" href={directions} target="_blank" rel="noreferrer">Itinéraire</Button>
            </>
          )}
          {a === 'AVAILABLE' && (
            <Button variant="contained" size="large" disabled={busy} onClick={reserve} sx={{ py: 1.5, fontWeight: 700 }}>
              Réserver cet établissement
            </Button>
          )}
          {a === 'AVAILABLE' && (
            <Typography variant="body2" color="text.secondary">
              Pendant la réservation, aucun autre ambassadeur ne peut le démarcher.
            </Typography>
          )}
          {(a === 'RESERVED_BY_ME' || a === 'FOLLOW_UP_MINE') && !creating && (
            <Button variant="outlined" disabled={busy} onClick={() => setCreating(true)}>Créer l’établissement</Button>
          )}
          {creating && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Créer l’établissement</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Pour installer la borne tout de suite. L’établissement est créé à votre nom avec les informations de cette fiche,
                puis transféré au gérant : il reçoit une invitation et l’accepte avec son compte.
              </Typography>
              <TextField fullWidth size="small" type="email" label="E-mail du gérant (facultatif)" value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)} helperText="Vous pourrez aussi l’inviter plus tard."
                inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }} />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="contained" disabled={busy} onClick={createFacility}>Créer</Button>
                <Button disabled={busy} onClick={() => setCreating(false)}>Annuler</Button>
              </Stack>
            </Paper>
          )}
          {a === 'RESERVED_BY_ME' && (
            <Stack direction="row" spacing={1}>
              <Button sx={{ flex: 1 }} disabled={busy} onClick={extend}>Prolonger</Button>
              <Button sx={{ flex: 1 }} color="error" disabled={busy} onClick={release}>Annuler</Button>
            </Stack>
          )}
        </Stack>
      )}

      {/* History */}
      <Divider sx={{ mb: 1 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Visites précédentes</Typography>
      {p.history.length === 0 && <Typography variant="body2" color="text.secondary">Jamais visité.</Typography>}
      <Stack spacing={1}>
        {p.history.map((h) => (
          <Box key={h.visitId}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {new Date(h.checkinAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' · '}{h.mine ? 'vous' : 'un autre ambassadeur'} · {RESULT_LABEL[h.result] ?? 'sans résultat'}
            </Typography>
            {h.comment && <Typography variant="body2" color="text.secondary">« {h.comment} »</Typography>}
          </Box>
        ))}
      </Stack>
    </Container>
  );
}
